// Global AI assistant — floating chat widget pinned bottom-right of every
// page in the CRM. Auto-detects the current route to scope the conversation
// (Sequences page → sequence-portfolio context, Contact detail → that
// contact's profile + activity, etc.). The agent is RECOMMEND-ONLY — see
// src/crm/ai/assistant.ts for the system-prompt guardrails.
//
// FUTURE (RBAC): when we add multiple CRM users, gate which scopes a user
// can ask about based on their role. Today it's single-admin, so the auth
// gate is just the existing CRM session.

import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { Zap, X, Send, Loader2, RotateCcw, Move, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useModKey } from "@/hooks/useModKey";

// Assistant brand: "Lumen" — a nod to LuzGrid (luz = light) + the "spark
// of insight" feel. Logo glyph uses the lightning bolt (Zap) so the
// floating button reads as energy/power, not generic chat-bot.
const ASSISTANT_NAME = "Lumen";
const ASSISTANT_TAGLINE = "your CRM co-pilot";

// Chat persistence — survives page reloads, route changes, and browser
// closes. Idle TTL: 60 min from last user/assistant message. After that
// expires, opening the panel starts fresh.
const CHAT_STORAGE_KEY = "lumen.chat.v1";
const IDLE_TTL_MS = 60 * 60 * 1000;

// ─── Corner positioning ──────────────────────────────────────────────
// Lumen used to be hardcoded to bottom-right. On dense pages with their
// own bottom-right floats (chat consoles, scroll-to-top buttons, dialog
// FABs), the button overlaps and obscures content. We keep the affordance
// simple: a "move" icon in the chat panel header cycles through the four
// viewport corners. Persisted to localStorage so the choice sticks across
// sessions on the same browser.
//
// Why four presets, not free-drag? Drag-and-drop adds touch handling,
// pointer-capture, snap-to-bounds on resize, and z-stacking edge cases.
// Four corners cover the actual problem ("Lumen is in my way") with a
// fraction of the code and zero bug surface.
type Corner = "br" | "bl" | "tl" | "tr";
const POSITION_KEY = "lumen.position.v1";
const POSITION_CYCLE: readonly Corner[] = ["br", "bl", "tl", "tr"] as const;
const POSITION_CLASSES: Record<Corner, string> = {
  br: "bottom-5 right-5",
  bl: "bottom-5 left-5",
  tl: "top-5 left-5",
  tr: "top-5 right-5",
};
const POSITION_LABELS: Record<Corner, string> = {
  br: "bottom-right",
  bl: "bottom-left",
  tl: "top-left",
  tr: "top-right",
};
function loadPosition(): Corner {
  if (typeof localStorage === "undefined") return "br";
  const v = localStorage.getItem(POSITION_KEY);
  return v === "br" || v === "bl" || v === "tl" || v === "tr" ? v : "br";
}
function savePosition(p: Corner): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(POSITION_KEY, p); } catch { /* quota / disabled */ }
}
interface PersistedChat {
  messages: ChatMessage[];
  lastActivityMs: number;
}
function loadPersisted(): ChatMessage[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PersistedChat;
    if (!parsed?.messages || !Array.isArray(parsed.messages)) return [];
    if (Date.now() - parsed.lastActivityMs > IDLE_TTL_MS) {
      // Expired — clear and start fresh
      localStorage.removeItem(CHAT_STORAGE_KEY);
      return [];
    }
    return parsed.messages;
  } catch {
    return [];
  }
}
// ─── Markdown rendering ───────────────────────────────────────────────
//
// Claude returns markdown in chat replies. Render it inline so headings,
// bullets, bold, links, etc. show as formatted text instead of literal
// `**bold**` / `## heading` characters. Tiny hand-rolled renderer —
// avoids a dependency for a feature that only needs ~7 patterns.

function escapeForChat(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderInline(s: string): string {
  return s
    // Inline code first so its contents don't get bold/italic-mangled
    .replace(/`([^`]+)`/g, '<code class="rounded bg-surface-panel2 px-1 py-0.5 font-mono text-[11px]">$1</code>')
    // Bold (greedy, must run before italic so **strong** stays one unit)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic — single asterisks
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-brand-deep underline">$1</a>');
}

function renderAssistantMarkdown(md: string): string {
  const escaped = escapeForChat(md);
  const lines = escaped.split("\n");
  const out: string[] = [];
  let listKind: "ul" | "ol" | null = null;
  const closeList = () => { if (listKind) { out.push(`</${listKind}>`); listKind = null; } };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Horizontal rule
    if (/^---+\s*$/.test(line)) {
      closeList();
      out.push('<hr class="my-3 border-line"/>');
      continue;
    }
    // Headings — H2 / H3 levels are what Claude usually emits
    const h3 = /^## (.+)$/.exec(line);
    if (h3) {
      closeList();
      out.push(`<h3 class="font-display text-sm font-semibold text-ink mt-3 mb-1.5">${renderInline(h3[1])}</h3>`);
      continue;
    }
    const h4 = /^### (.+)$/.exec(line);
    if (h4) {
      closeList();
      out.push(`<h4 class="font-display text-[13px] font-semibold text-ink mt-2 mb-1">${renderInline(h4[1])}</h4>`);
      continue;
    }
    // Bullet list
    if (/^[-*] (.+)$/.test(line)) {
      if (listKind !== "ul") { closeList(); out.push('<ul class="list-disc pl-5 space-y-0.5 my-1.5">'); listKind = "ul"; }
      out.push(`<li>${renderInline(line.replace(/^[-*] /, ""))}</li>`);
      continue;
    }
    // Numbered list
    if (/^\d+\. (.+)$/.test(line)) {
      if (listKind !== "ol") { closeList(); out.push('<ol class="list-decimal pl-5 space-y-0.5 my-1.5">'); listKind = "ol"; }
      out.push(`<li>${renderInline(line.replace(/^\d+\. /, ""))}</li>`);
      continue;
    }
    // Blank line — paragraph break
    if (line.trim() === "") {
      closeList();
      // Don't emit empty <p> — the natural margin between blocks handles it
      continue;
    }
    // Plain paragraph
    closeList();
    out.push(`<p class="my-1.5">${renderInline(line)}</p>`);
  }
  closeList();
  return out.join("");
}

function persist(messages: ChatMessage[]) {
  if (typeof localStorage === "undefined") return;
  try {
    if (messages.length === 0) {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      return;
    }
    const payload: PersistedChat = { messages, lastActivityMs: Date.now() };
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(payload));
  } catch { /* localStorage full / disabled — silent fail, chat just doesn't survive reload */ }
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

/**
 * Map the current URL to a backend `scope` string. The backend dispatches
 * to a context builder per scope. Routes that don't have a specialized
 * builder fall back to the "general" scope (CRM topline stats).
 */
function deriveScope(pathname: string): { scope: string; entityId?: string } {
  if (pathname.startsWith("/sequences/queue")) return { scope: "queue" };
  if (pathname.startsWith("/sequences"))       return { scope: "sequences" };
  if (pathname.startsWith("/deals/calculator")) return { scope: "calculator" };
  // /contacts/:id — entity-specific context
  const contactMatch = /^\/contacts\/([^/]+)/.exec(pathname);
  if (contactMatch && contactMatch[1] !== "new") {
    return { scope: "contact", entityId: contactMatch[1] };
  }
  return { scope: "general" };
}

function serializeCalcState(): string | undefined {
  try {
    const raw = localStorage.getItem("lg.calc.draft");
    if (!raw) return undefined;
    const d = JSON.parse(raw) as {
      env?: { workstations: number; users: number; servers: number; devices: number };
      services?: { name: string; cost: number; type: string; tiers: Record<string, boolean> }[];
      labor?: { name: string; rate: number; hours: number; basis: string; tiers: Record<string, boolean> }[];
      risk?: number;
      profitMode?: string;
      marginPct?: number;
      dollarProfit?: number;
      recommended?: string;
      discounts?: { y2: number; y3: number; y5: number };
    };
    const lines: string[] = [];
    if (d.env) {
      lines.push(`Client environment: ${d.env.workstations} workstations, ${d.env.users} users, ${d.env.servers} servers, ${d.env.devices} devices`);
    }
    if (d.services?.length) {
      lines.push(`\nServices (${d.services.length}):`);
      for (const s of d.services) {
        const tiers = Object.entries(s.tiers).filter(([, v]) => v).map(([k]) => k[0].toUpperCase()).join("/");
        lines.push(`- ${s.name}: $${s.cost}/${s.type.replace("per_", "")} [${tiers}]`);
      }
    }
    if (d.labor?.length) {
      lines.push(`\nLabor (${d.labor.length}):`);
      for (const l of d.labor) {
        const tiers = Object.entries(l.tiers).filter(([, v]) => v).map(([k]) => k[0].toUpperCase()).join("/");
        lines.push(`- ${l.name}: $${l.rate}/hr × ${l.hours}hrs/${l.basis.replace("per_", "")} [${tiers}]`);
      }
    }
    if (d.risk != null) lines.push(`\nRisk multiplier: ${d.risk}x`);
    if (d.profitMode === "margin") lines.push(`Profit mode: margin ${d.marginPct ?? 30}%`);
    else if (d.profitMode === "dollar") lines.push(`Profit mode: flat $${d.dollarProfit ?? 0}`);
    if (d.recommended) lines.push(`Recommended tier: ${d.recommended}`);
    if (d.discounts) lines.push(`Multi-year discounts: 2yr=${d.discounts.y2}%, 3yr=${d.discounts.y3}%, 5yr=${d.discounts.y5}%`);
    return lines.length ? lines.join("\n") : undefined;
  } catch {
    return undefined;
  }
}

/** Suggested first-message prompts per scope — gives users an obvious
 *  starting point without making them stare at a blank input. */
function suggestionsFor(scope: string): string[] {
  switch (scope) {
    case "sequences":
      return [
        "Which sequence is performing best?",
        "What verticals should I open up next?",
        "Why is my CPA sequence bouncing?",
      ];
    case "queue":
      return [
        "How's the queue looking today?",
        "Anything that needs my attention?",
        "Are any sequences underperforming?",
      ];
    case "contact":
      return [
        "Should I follow up with this person?",
        "What does their engagement look like?",
        "What's the next best action here?",
      ];
    case "calculator":
      return [
        "Does this pricing make sense?",
        "How do my margins compare to industry?",
        "What should I include in Pro vs Enterprise?",
      ];
    default:
      return [
        "How's my pipeline today?",
        "What should I focus on?",
        "Any sequences worth pausing?",
      ];
  }
}

export default function AiAssistant() {
  const mod = useModKey();
  const location = useLocation();
  const params = useParams();
  void params; // useParams gives us route params but we read them from pathname above

  const { scope, entityId } = useMemo(() => deriveScope(location.pathname), [location.pathname]);

  // Read the per-org toggle. Endpoint is admin-only; for non-admin users
  // the call 401s and we hide the widget. For admin users we honor the
  // explicit flag (default ON if the row exists).
  //
  // Cached for the session — admin can refresh after toggling.
  const { data: aiSettings } = useQuery({
    queryKey: ["ai", "settings", "ui-flag"],
    queryFn: async () => {
      const r = await fetch("/crm/api/admin/ai/settings", { credentials: "include" });
      if (!r.ok) return null;
      return r.json() as Promise<{ feat_assistant_widget?: boolean; is_enabled?: boolean }>;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const widgetEnabled = aiSettings == null
    ? true   // non-admin or settings unavailable → assume enabled (the assist endpoint will gate properly)
    : !!(aiSettings.is_enabled && aiSettings.feat_assistant_widget);

  const [open, setOpen] = useState(false);
  // Initialize from localStorage so the chat survives reloads + browser
  // closes within the 60-min idle window.
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadPersisted());
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Corner position — read once on mount, then user toggles via the
  // header "move" button. Persisted to localStorage so it sticks.
  const [position, setPosition] = useState<Corner>(() => loadPosition());
  function cyclePosition(): void {
    setPosition((cur) => {
      const idx = POSITION_CYCLE.indexOf(cur);
      const next = POSITION_CYCLE[(idx + 1) % POSITION_CYCLE.length];
      savePosition(next);
      return next;
    });
  }

  // Per-session hide — when Lumen is genuinely in the way RIGHT NOW
  // (e.g. an export dialog landed under it), the user can banish the
  // widget for the remainder of the session. Reload restores it. We
  // deliberately DON'T persist this — losing access to Lumen forever
  // because of one click is a worse failure mode than re-hiding it
  // each session if you really want it gone permanently (in which case
  // the admin AI-settings toggle is the right tool).
  const [hidden, setHidden] = useState(false);
  function hideForSession(): void {
    setOpen(false);
    setHidden(true);
  }

  // Persist on every message change so the next mount can recover.
  useEffect(() => { persist(messages); }, [messages]);

  // Persist across route changes — Lumen's data context updates per page
  // (worker rebuilds it fresh), but the conversation history stays so the
  // user can ask "now compare to the deals page" without losing thread.
  // Errors clear when scope changes so a stale error doesn't follow the
  // user to a new page.
  useEffect(() => { setError(null); }, [scope, entityId]);

  // Autoscroll to bottom on new message.
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  // Focus input when panel opens.
  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  // ALL HOOKS MUST RUN BEFORE THIS POINT. Moved 2026-05-13 — previously
  // the three useEffect blocks above (setError clear / autoscroll / focus)
  // sat AFTER these early returns, which made the hook count jump from
  // N→N+3 the moment the widget was enabled or unhidden, triggering
  // minified React error #310.
  if (!widgetEnabled) return null;
  if (hidden) return null;

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    setPending(true);
    setError(null);
    try {
      let page_context: string | undefined;
      if (scope === "calculator") {
        page_context = serializeCalcState();
      }
      const res = await fetch("/crm/api/ai/assist", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next, scope, entity_id: entityId, page_context }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = (body as { error?: string; message?: string }).message
          ?? (body as { error?: string }).error
          ?? `HTTP ${res.status}`;
        setError(msg);
        // Keep the user's message in the history; let them retry.
        return;
      }
      const reply = (body as { reply?: string }).reply ?? "(no reply)";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {/* Subtle Lumen launcher — calm chip with purple icon, not a giant
          gradient FAB. Per MASTER_PROMPT §2.4, the floating purple FAB is
          gone; what remains is a small launcher for the side panel. The
          inline `✦ Lumen` buttons next to text fields are added as a
          separate primitive (LumenButton) so this surface stays optional. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "group fixed z-40 inline-flex h-9 items-center gap-1.5 rounded-full border border-border bg-bg-card pl-2 pr-3 text-base font-medium text-text-2 shadow-1 transition-colors duration-[120ms] ease-lz hover:border-ai/30 hover:bg-bg-hover hover:text-text",
            POSITION_CLASSES[position],
          )}
          aria-label={`Open ${ASSISTANT_NAME}, ${ASSISTANT_TAGLINE}`}
          title={`${ASSISTANT_NAME} · ${ASSISTANT_TAGLINE} (recommend-only)`}
        >
          <span className="grid h-5 w-5 place-items-center rounded-full bg-ai-soft text-ai">
            <Zap className="h-3 w-3" />
          </span>
          <span className="font-display tracking-tight">{ASSISTANT_NAME}</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className={cn(
          "fixed z-40 flex h-[560px] w-[400px] max-h-[calc(100vh-2.5rem)] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-line bg-surface-panel shadow-[0_24px_48px_-12px_rgba(11,18,32,0.25)]",
          POSITION_CLASSES[position],
        )}>
          {/* Header */}
          <header
            className="flex items-center gap-2 border-b border-line px-4 py-3"
            style={{
              backgroundImage: "linear-gradient(135deg, var(--email-ai-soft), transparent)",
            }}
          >
            <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-[var(--email-ai-gradient-from)] to-[var(--email-ai-gradient-to)] text-white shadow-[var(--shadow-soft)]">
              <Zap className="h-4 w-4 fill-white" />
            </div>
            <div className="flex-1">
              <p className="font-display text-sm font-semibold text-ink">{ASSISTANT_NAME}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-ink-dim">
                {scopeLabel(scope)} {entityId && `· ${entityId.slice(0, 8)}…`}
              </p>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                onClick={() => { setMessages([]); setError(null); }}
                className="rounded p-1 text-ink-dim hover:bg-surface-hover hover:text-ink"
                aria-label="New chat"
                title="Start a new chat (current conversation will be cleared)"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={cyclePosition}
              className="rounded p-1 text-ink-dim hover:bg-surface-hover hover:text-ink"
              aria-label="Move Lumen to a different corner"
              title={`Move Lumen — currently ${POSITION_LABELS[position]}, click to cycle corners`}
            >
              <Move className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={hideForSession}
              className="rounded p-1 text-ink-dim hover:bg-surface-hover hover:text-ink"
              aria-label="Hide Lumen for this session"
              title="Hide Lumen for this session (reload the page to bring it back)"
            >
              <EyeOff className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-ink-dim hover:bg-surface-hover hover:text-ink"
              aria-label="Close assistant"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 dialog-scroll">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col gap-3 px-1">
                <p className="text-sm text-ink-muted">
                  Hi, I'm {ASSISTANT_NAME}. Ask about the data on this page —
                  I'll analyze and recommend, but I won't make changes for
                  you.
                </p>
                <div className="space-y-1.5">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-ink-dim">Try</p>
                  {suggestionsFor(scope).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="block w-full rounded-lg border border-line bg-surface-panel2/40 px-3 py-2 text-left text-xs text-ink hover:border-[var(--email-ai)]/40 hover:bg-[var(--email-ai-soft)]/40"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ol className="space-y-3">
                {messages.map((m, i) => (
                  <li
                    key={i}
                    className={cn(
                      "rounded-lg px-3 py-2 text-sm leading-relaxed",
                      m.role === "user"
                        ? "ml-auto max-w-[85%] bg-brand-deep text-white"
                        : "max-w-[92%] bg-surface-panel2 text-ink",
                    )}
                  >
                    {m.role === "user" ? (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    ) : (
                      <div
                        className="lumen-md"
                        dangerouslySetInnerHTML={{ __html: renderAssistantMarkdown(m.content) }}
                      />
                    )}
                  </li>
                ))}
                {pending && (
                  <li className="max-w-[92%] rounded-lg bg-surface-panel2 px-3 py-2 text-sm text-ink-muted">
                    <Loader2 className="inline h-3.5 w-3.5 animate-spin" />{" "}
                    Thinking…
                  </li>
                )}
              </ol>
            )}
            {error && (
              <p className="mt-2 rounded bg-rose-50 px-2 py-1.5 text-xs text-rose-700">
                {error}
              </p>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="border-t border-line bg-surface-panel p-2"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                placeholder="Ask anything about this page…"
                rows={2}
                className="flex-1 resize-none rounded-lg border border-line bg-surface-input px-2.5 py-2 text-sm leading-relaxed text-ink placeholder:text-ink-dim focus:outline-none focus:ring-2 focus:ring-[var(--email-ai)]/40"
              />
              <button
                type="submit"
                disabled={pending || !input.trim()}
                className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-[var(--email-ai-gradient-from)] to-[var(--email-ai-gradient-to)] text-white shadow-[var(--shadow-soft)] disabled:opacity-50"
                aria-label="Send"
              >
                {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1.5 px-1 text-[10px] text-ink-dim">
              {ASSISTANT_NAME} is recommend-only · context: {scopeLabel(scope)} · {mod}↩ to send
            </p>
          </form>
        </div>
      )}
    </>
  );
}

function scopeLabel(scope: string): string {
  const labels: Record<string, string> = {
    sequences: "Sequences",
    queue:     "Outbox",
    contact:   "Contact",
    general:   "General",
  };
  return labels[scope] ?? scope;
}
